/**
 * Build-time git branch chip rendered next to the TopStrip tagline.
 *
 * Implements the UI half of the DevSync branch-aware capture/export plan
 * (`docs/runbooks/dev_sync_and_fix.md` §7): every page already carries the
 * branch via `APP_GIT_BRANCH` (injected by `vite.config.ts`), but normal
 * end users shouldn't see infrastructure detail on production. So:
 *
 *   • Platform-role users (dev / owner / admin / ops): always visible
 *     (they want to know what they're testing).
 *   • Everyone else: visible only when the branch is **not** `main`
 *     (preview / feature builds — useful signal for pilots, hidden for
 *     production traffic).
 */
import { APP_GIT_BRANCH, isDefaultBranch } from "@/config/version";
import { useAuth } from "@/contexts/AuthContext";

export function TopStripBranchChip() {
  const auth = useAuth();
  const isPlatform = auth.isPlatformOperator;
  const onDefault = isDefaultBranch(APP_GIT_BRANCH);
  if (!isPlatform && onDefault) return null;
  if (!APP_GIT_BRANCH || APP_GIT_BRANCH === "unknown") return null;

  const tone = onDefault
    ? "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700"
    : "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-800";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium tracking-normal normal-case ${tone}`}
      title={`Build-time git branch: ${APP_GIT_BRANCH}`}
    >
      <span aria-hidden="true">⎇</span>
      <span>branch: {APP_GIT_BRANCH}</span>
    </span>
  );
}
